/**
 * Outbox for messages to the PersonAgent socket (M5.1). Every message the person sends is
 * queued here first; it leaves when the socket is open and is marked delivered when the server
 * acknowledges it with the event it stored. A message without an acknowledgement after
 * `timeoutMs` is "not delivered" and can be retried; the queue flushes again on every reconnect.
 *
 * Pure state machine (no React, no WebSocket) so the transitions are unit-tested.
 */

export type DeliveryState = 'sending' | 'delivered' | 'failed';

export interface OutboxEntry<T> {
  client_id: string;
  payload: T;
  state: DeliveryState;
  /** When the message was first queued (ms since epoch). */
  queued_at: number;
  /** Last time the payload went down a socket, if ever. */
  sent_at?: number | undefined;
  /** Attempts so far (0 while still waiting for a socket). */
  attempts: number;
}

export interface OutboxHooks<T> {
  /** Put the payload on the wire. Returns false when no socket is open. Replaceable with setTransmit. */
  transmit?: ((payload: T) => boolean) | undefined;
  /** Called on every state change of an entry. */
  onChange: (entry: OutboxEntry<T>) => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export const DELIVERY_TIMEOUT_MS = 10_000;

export class Outbox<T> {
  private readonly entries = new Map<string, OutboxEntry<T>>();
  private readonly timers = new Map<string, unknown>();
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  private transmit: (payload: T) => boolean;

  constructor(
    private readonly hooks: OutboxHooks<T>,
    private readonly timeoutMs = DELIVERY_TIMEOUT_MS,
  ) {
    this.transmit = hooks.transmit ?? (() => false);
    this.now = hooks.now ?? (() => Date.now());
    this.setTimer = hooks.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = hooks.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  /** The socket hook's send function, once it exists (the React store sets it in an effect). */
  setTransmit(fn: (payload: T) => boolean): void {
    this.transmit = fn;
  }

  /** Queue a message and try to send it at once. */
  enqueue(client_id: string, payload: T): OutboxEntry<T> {
    const entry: OutboxEntry<T> = {
      client_id,
      payload,
      state: 'sending',
      queued_at: this.now(),
      attempts: 0,
    };
    this.entries.set(client_id, entry);
    this.arm(entry);
    this.attempt(entry);
    this.hooks.onChange({ ...entry });
    return { ...entry };
  }

  /** The server stored the message: delivered, timer cleared. */
  acknowledge(client_id: string): boolean {
    const entry = this.entries.get(client_id);
    if (!entry) return false;
    this.disarm(client_id);
    entry.state = 'delivered';
    this.entries.delete(client_id);
    this.hooks.onChange({ ...entry });
    return true;
  }

  /** The person pressed Retry on a failed message: back to sending, one more attempt. */
  retry(client_id: string): boolean {
    const entry = this.entries.get(client_id);
    if (!entry || entry.state !== 'failed') return false;
    entry.state = 'sending';
    entry.queued_at = this.now();
    this.arm(entry);
    this.attempt(entry);
    this.hooks.onChange({ ...entry });
    return true;
  }

  /** The socket (re)opened: every message still sending goes out again. */
  flush(): number {
    let sent = 0;
    for (const entry of this.entries.values()) {
      if (entry.state !== 'sending') continue;
      if (this.attempt(entry)) sent += 1;
    }
    return sent;
  }

  get(client_id: string): OutboxEntry<T> | undefined {
    const entry = this.entries.get(client_id);
    return entry ? { ...entry } : undefined;
  }

  /** Entries still sending or failed. */
  pending(): OutboxEntry<T>[] {
    return [...this.entries.values()].map((e) => ({ ...e }));
  }

  private attempt(entry: OutboxEntry<T>): boolean {
    const ok = this.transmit(entry.payload);
    if (ok) {
      entry.attempts += 1;
      entry.sent_at = this.now();
    }
    return ok;
  }

  private arm(entry: OutboxEntry<T>): void {
    this.disarm(entry.client_id);
    const handle = this.setTimer(() => {
      const current = this.entries.get(entry.client_id);
      if (!current || current.state !== 'sending') return;
      current.state = 'failed';
      this.timers.delete(entry.client_id);
      this.hooks.onChange({ ...current });
    }, this.timeoutMs);
    this.timers.set(entry.client_id, handle);
  }

  private disarm(client_id: string): void {
    const handle = this.timers.get(client_id);
    if (handle !== undefined) this.clearTimer(handle);
    this.timers.delete(client_id);
  }
}
